import * as THREE from 'three'
import { ColladaLoader } from 'three/examples/jsm/loaders/ColladaLoader.js'
import type { RenderRecipe } from './types'

/**
 * How the cover mesh was identified. 'collada-node' is the exact path and what every
 * shipped model resolves through; anything else means the wrapper and the embedded
 * COLLADA disagree and a fallback carried the render (worth a console warning).
 */
export type CoverMatch =
  | 'collada-node'
  | 'geometry-name'
  | 'geometry-name-partial'
  | 'material-name'
  | 'largest-mesh'
  | 'none'

export interface BuiltModel {
  /** The loaded COLLADA scene (carries the baked up-axis + unit-scale matrix). */
  group: THREE.Group
  /** Materials of the cover mesh(es) — where the skin texture is applied. */
  coverMaterials: THREE.MeshPhongMaterial[]
  /** Number of meshes identified as the cover (for diagnostics/tests). */
  coverMeshCount: number
  /** Which resolution step found the cover (for diagnostics/tests). */
  matchedBy: CoverMatch
  /** Every mesh name in the model — the useful detail when resolution fails. */
  meshNames: string[]
  dispose(): void
}

const normalize = (s: string | null | undefined) => (s ?? '').toLowerCase().replace(/[^a-z0-9]/g, '')

const toArray = (m: THREE.Material | THREE.Material[]): THREE.Material[] => (Array.isArray(m) ? m : [m])

/** Names of this object and all its ancestors, normalized. */
function ancestorNames(obj: THREE.Object3D): string[] {
  const names: string[] = []
  let cur: THREE.Object3D | null = obj
  while (cur) {
    if (cur.name) names.push(normalize(cur.name))
    cur = cur.parent
  }
  return names
}

/**
 * Parse the embedded COLLADA and prepare it for skinning:
 *  - resolve the cover mesh(es) from the recipe's coverMeshName (the document-
 *    material link), tolerant of ColladaLoader naming the object after the node
 *    vs the geometry, and including useParentMaterial sub-meshes (snapbook).
 *  - clone every material (break shared instances) so texturing the cover never
 *    bleeds onto the page block / staples.
 *  - prep cover materials for the skin (white base, double-sided).
 */
export function buildModel(recipe: RenderRecipe, thickness = 1): BuiltModel {
  const loader = new ColladaLoader()
  const collada = loader.parse(recipe.colladaString, '')
  const group = collada.scene as unknown as THREE.Group

  // Spine/depth scaling (e.g. softcover thickness ∝ page count): scale the model's
  // thinnest axis. The front/back covers are perpendicular to it, so they don't
  // distort — only the spine widens and the covers move apart.
  if (thickness !== 1) {
    group.updateMatrixWorld(true)
    const size = new THREE.Box3().setFromObject(group).getSize(new THREE.Vector3())
    const axis = size.x <= size.y && size.x <= size.z ? 'x' : size.y <= size.z ? 'y' : 'z'
    group.scale[axis] *= thickness
    group.updateMatrixWorld(true)
  }

  const meshes: THREE.Mesh[] = []
  group.traverse((o) => {
    if ((o as THREE.Mesh).isMesh) meshes.push(o as THREE.Mesh)
  })

  // Break any shared material instances up front.
  for (const mesh of meshes) {
    mesh.material = Array.isArray(mesh.material)
      ? mesh.material.map((m) => m.clone())
      : mesh.material.clone()
  }

  // --- Resolve the cover mesh(es) ---
  // Layered on purpose. The COLLADA-resolved node name (recipe.coverNodeNames) is
  // exact and is how all 26 shipped models resolve; the name/material heuristics stay
  // as a net for a model whose wrapper and COLLADA disagree; and the largest-mesh last
  // resort exists so an unrecognized model renders a skinned book rather than the
  // silent blank one this used to produce.
  const target = normalize(recipe.coverMeshName)
  // "object003mesh" -> "object003", "object003mesh001" -> "object003001": strip the
  // "Mesh" the wrapper appends, keeping any COLLADA duplicate suffix (".001").
  const targetNode = target.replace(/mesh(\d*)$/, '$1')
  const coverNameBase = normalize(recipe.coverMaterialName).replace(/\d+$/, '') // "cover1" -> "cover"
  const resolved = new Set(recipe.coverNodeNames.map(normalize).filter(Boolean))

  const named = (mesh: THREE.Mesh, pred: (n: string) => boolean) => ancestorNames(mesh).some(pred)
  const matchesMaterial = (mesh: THREE.Mesh) =>
    coverNameBase.length >= 3 && toArray(mesh.material).some((m) => normalize(m.name).includes(coverNameBase))

  let matchedBy: CoverMatch = 'none'
  let coverMeshes: THREE.Mesh[] = []
  const step = (by: CoverMatch, find: () => THREE.Mesh[]) => {
    if (coverMeshes.length) return
    const found = find()
    if (found.length) {
      coverMeshes = found
      matchedBy = by
    }
  }

  step('collada-node', () => (resolved.size ? meshes.filter((m) => named(m, (n) => resolved.has(n))) : []))
  step('geometry-name', () =>
    meshes.filter((m) => named(m, (n) => n === target || (targetNode.length >= 3 && n === targetNode))),
  )
  step('geometry-name-partial', () =>
    meshes.filter((m) => named(m, (n) => targetNode.length >= 3 && n.includes(targetNode))),
  )
  step('material-name', () => meshes.filter(matchesMaterial))
  step('largest-mesh', () => {
    // The cover is the largest thing in the model that isn't the page block. Compare
    // the two biggest world-space dimensions (the cover's face) rather than volume, so
    // a thick page block can't outrank a thin cover.
    group.updateMatrixWorld(true)
    const size = new THREE.Vector3()
    const best = meshes
      .filter((m) => !/^(book|page|staple|cube)/.test(normalize(m.name)))
      .map((m) => {
        new THREE.Box3().setFromObject(m).getSize(size)
        const [a, b] = [size.x, size.y, size.z].sort((x, y) => y - x)
        return { mesh: m, face: a * b }
      })
      .sort((a, b) => b.face - a.face)[0]
    return best ? [best.mesh] : []
  })

  // Prep cover materials: white diffuse (so the skin isn't tinted), double-sided,
  // and slightly glossy. The specular highlight slides across the cover's curves as
  // it rotates, which is what reveals the contours that flat ambient light hides
  // (a laminated yearbook cover is glossy anyway).
  const coverMaterials: THREE.MeshPhongMaterial[] = []
  for (const mesh of coverMeshes) {
    for (const mat of toArray(mesh.material)) {
      const phong = mat as THREE.MeshPhongMaterial
      phong.side = THREE.DoubleSide
      if (phong.color) phong.color.setRGB(1, 1, 1)
      phong.shininess = 24 // broad sheen reveals gentle bows better than a tight highlight
      if (phong.specular) phong.specular.setRGB(0.26, 0.26, 0.26)
      phong.needsUpdate = true
      coverMaterials.push(phong)
    }
  }

  const dispose = () => {
    const textures = new Set<THREE.Texture>()
    group.traverse((o) => {
      const mesh = o as THREE.Mesh
      if (!mesh.isMesh) return
      mesh.geometry?.dispose()
      for (const mat of toArray(mesh.material)) {
        const m = mat as THREE.MeshPhongMaterial
        if (m.map) textures.add(m.map)
        m.dispose()
      }
    })
    textures.forEach((t) => t.dispose())
  }

  return {
    group,
    coverMaterials,
    coverMeshCount: coverMeshes.length,
    matchedBy,
    meshNames: meshes.map((m) => m.name),
    dispose,
  }
}

/**
 * Apply (or swap) the cover skin. Disposes the previously-applied texture to keep
 * GPU memory bounded across skin swaps in a long-lived iframe.
 */
export function setCoverTexture(model: BuiltModel, texture: THREE.Texture): void {
  const previous = new Set<THREE.Texture>()
  for (const mat of model.coverMaterials) {
    if (mat.map && mat.map !== texture) previous.add(mat.map)
    mat.map = texture
    mat.needsUpdate = true
  }
  previous.forEach((t) => t.dispose())
}
